import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Handshake, MapPin, Package, MessagesSquare, Ship, Star } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Reveal, SectionHead, CTABand, TickList } from "@/components/ui-bits";
import { useSettings, useCategories, useProducts, useFaqs, usePosts, useTestimonials } from "@/hooks/useContent";
import { HERO_IMAGE, COMPANY, waLink, track, WHO_WE_SERVE, WHY_ORVELLA, PROCESS_STEPS } from "@/lib/site";

const ICONS = { MapPin, Handshake, Package, Ship, MessagesSquare };

const MARQUEE_ITEMS = [
  "1121 Basmati Rice", "1509 Basmati Rice", "IR 64 Rice", "Whole Spices", "Spice Powders",
  "Makhana / Fox Nuts", "Peanuts", "Assam Tea", "Darjeeling Tea",
  "Dehydrated Fruits & Vegetables", "Beetroot Powder", "Banana Powder",
  "Ginger Powder", "Onion Powder", "Tomato Powder", "Garlic Powder",
];

function MarqueeStrip() {
  const row = (hidden) => (
    <div className="flex" aria-hidden={hidden || undefined}>
      {MARQUEE_ITEMS.map((item) => (
        <span key={item} className="flex items-center gap-8 pr-8">
          <span className="whitespace-nowrap font-serif text-lg italic text-navy/75">{item}</span>
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" aria-hidden="true" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="overflow-hidden border-b border-line bg-white py-4" data-testid="product-marquee">
      <div className="marquee-track flex w-max">
        {row(false)}
        {row(true)}
      </div>
    </div>
  );
}

export default function Home() {
  const { data: settings } = useSettings();
  const { data: categories } = useCategories();
  const { data: featured } = useProducts({ featured: true });
  const { data: faqs } = useFaqs();
  const { data: posts } = usePosts();
  const { data: testimonials } = useTestimonials();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 700], [1.08, 1.2]);

  const faqJsonld = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (faqs || []).slice(0, 10).map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <>
      <Seo
        title="Orvella Overseas — Indian Food Products Exporter & Global B2B Supplier"
        description="Orvella Overseas Private Limited connects international buyers with quality food and agricultural products sourced from India — rice, spices, makhana, tea, peanuts, dehydrated products and food powders."
        path="/"
        image={HERO_IMAGE}
        jsonld={faqJsonld}
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <img src={HERO_IMAGE} alt="Cargo ship loaded with containers sailing at sea during daytime" className="h-full w-full object-cover" loading="eager" fetchpriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/85 to-ivory/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-ivory via-transparent to-ivory/40" />
        </motion.div>
        <div className="container-x relative flex min-h-[84vh] items-center py-20 md:py-28">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/70 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-navy backdrop-blur"
            >
              India <ArrowRight size={12} className="text-gold-dark" /> Global Markets · B2B Supply
            </motion.p>
            <h1 className="mt-6 font-serif text-4xl font-semibold leading-[1.06] text-navy sm:text-6xl lg:text-[4.1rem]" data-testid="hero-headline">
              {["Connecting India's", "Quality Food Products", "to Global Markets"].map((line, i) => (
                <span key={line} className="mask-line">
                  <motion.span
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                    {i === 1 ? <span className="text-gold-dark">.</span> : i === 2 ? <span className="text-gold-dark"> — </span> : " "}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 max-w-xl text-base leading-relaxed text-ink/80 md:text-lg"
            >
              Orvella Overseas Private Limited connects international buyers with carefully sourced food and agricultural
              products from India, supporting professional sourcing, packaging and export coordination.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/quote" data-testid="hero-request-quote-btn" className="btn-gold">
                Request a Quote <ArrowRight size={16} />
              </Link>
              <Link to="/products" data-testid="hero-explore-products-btn" className="btn-navy">
                Explore Products
              </Link>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                onClick={() => track("whatsapp_click")}
                className="btn-outline bg-white/70 backdrop-blur"
              >
                WhatsApp Us
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <MarqueeStrip />

      {/* TRUST STRIP */}
      <section className="border-y border-line bg-white" data-testid="trust-strip">
        <div className="container-x grid grid-cols-2 gap-x-6 gap-y-8 py-10 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { icon: MapPin, title: "Indian Sourcing", text: "Quality-focused sourcing from India" },
            { icon: Handshake, title: "B2B Supply", text: "Built for international buyers" },
            { icon: Package, title: "Flexible Packaging", text: "Packaging options based on buyer requirements" },
            { icon: Ship, title: "Export Coordination", text: "Professional order and shipment coordination" },
            { icon: MessagesSquare, title: "Buyer Support", text: "Responsive communication from enquiry to shipment" },
          ].map((t, i) => (
            <Reveal key={t.title} delay={i * 0.06} className="flex flex-col items-start gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sand text-gold-dark">
                <t.icon size={20} strokeWidth={1.8} />
              </span>
              <h3 className="text-sm font-bold text-navy">{t.title}</h3>
              <p className="text-xs leading-relaxed text-mist">{t.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRODUCT PORTFOLIO */}
      <section className="section-pad" data-testid="home-products-section">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHead
              eyebrow="Our Product Portfolio"
              title="Quality Food & Agricultural Products from India"
              sub="A growing B2B portfolio sourced across India — each product supported with specifications, packaging options and export coordination."
            />
            <Reveal delay={0.1}>
              <Link to="/products" data-testid="view-all-products-btn" className="btn-outline shrink-0">
                View All Products <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>

          <Reveal delay={0.12} className="mt-8 flex flex-wrap gap-2">
            {(categories || []).map((c) => (
              <Link key={c.slug} to={`/products?category=${c.slug}`} className="chip transition-colors hover:border-gold hover:text-gold-dark">
                {c.tagline || c.name}
              </Link>
            ))}
          </Reveal>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(featured || []).slice(0, 6).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="section-pad bg-sand" data-testid="who-we-serve-section">
        <div className="container-x">
          <SectionHead
            eyebrow="Who We Serve"
            title="Built for International B2B Buyers"
            sub="Our approach is designed around the businesses that source food and agricultural products from India at scale."
            center
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHO_WE_SERVE.map((w, i) => {
              const Icon = ICONS[w.icon] || Handshake;
              return (
                <Reveal key={w.title} delay={i * 0.05}>
                  <div className="card card-hover h-full p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy text-gold-soft">
                      <Icon size={21} strokeWidth={1.8} />
                    </span>
                    <h3 className="mt-4 font-serif text-lg font-semibold text-navy">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mist">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY ORVELLA */}
      <section className="section-pad" data-testid="home-why-orvella">
        <div className="container-x">
          <SectionHead
            eyebrow="Why Orvella"
            title="Why Work With Orvella Overseas?"
            sub={COMPANY + " — professional sourcing, clear communication and export coordination from enquiry to shipment."}
          />
          <div className="mt-12 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
            {WHY_ORVELLA.map((w, i) => {
              const Icon = ICONS[w.icon] || Handshake;
              return (
                <Reveal key={w.title} delay={i * 0.05}>
                  <div className="flex gap-4">
                    <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sand text-gold-dark">
                      <Icon size={19} strokeWidth={1.8} />
                    </span>
                    <div>
                      <h3 className="font-bold text-navy">{w.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-mist">{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.15} className="mt-10">
            <Link to="/why-orvella" className="text-sm font-bold uppercase tracking-wider text-gold-dark underline decoration-gold/50 underline-offset-4 hover:text-navy">
              More about working with us
            </Link>
          </Reveal>
        </div>
      </section>

      {/* EXPORT PROCESS */}
      <section className="section-pad bg-navy" data-testid="home-export-process">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow !text-gold-soft">Export Process</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 max-w-2xl font-serif text-3xl font-semibold text-ivory sm:text-4xl">From Enquiry to Shipment, in Eight Clear Steps</h2>
          </Reveal>
          <div className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.05}>
                <div className="border-t-2 border-gold/40 pt-4">
                  <span className="font-serif text-2xl font-semibold text-gold-soft">{s.n}</span>
                  <h3 className="mt-2 font-bold text-ivory">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ivory/65">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/export-process" className="text-sm font-bold uppercase tracking-wider text-gold-soft underline decoration-gold/50 underline-offset-4 hover:text-ivory">
              See the full export process
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MARKETS */}
      <section className="section-pad" data-testid="home-markets-section">
        <div className="container-x">
          <SectionHead
            eyebrow="Global Markets"
            title="From India to Global Markets"
            sub="Orvella Overseas is focused on serving international B2B buyers and developing long-term sourcing relationships across global markets."
          />
          <Reveal delay={0.1} className="mt-10 overflow-hidden rounded-xl border border-line bg-white">
            <img src={settings?.world_map_image || undefined} alt="" className="hidden" />
            <img src="https://static.prod-images.emergentagent.com/jobs/97e958b2-7e84-4380-9c02-6f4dc97bd961/images/07baa91f2a4ee5454de6720db57b00d89741ea65eaff848086f349b50956ab3b.jpeg" alt="World map showing Orvella Overseas target international markets" loading="lazy" className="h-auto w-full object-cover" />
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(settings?.markets || []).map((m, i) => (
              <Reveal key={m.name} delay={i * 0.04}>
                <div className="card card-hover h-full p-5">
                  <h3 className="font-bold text-navy">{m.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-mist">{m.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-8">
            <Link to="/markets" className="text-sm font-bold uppercase tracking-wider text-gold-dark underline decoration-gold/50 underline-offset-4 hover:text-navy">
              Explore our target markets
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      {(posts || []).length > 0 && (
        <section className="section-pad bg-sand" data-testid="home-insights-section">
          <div className="container-x">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <SectionHead eyebrow="Insights" title="Orvella Global Food Trade Insights" />
              <Reveal delay={0.1}>
                <Link to="/insights" className="btn-outline shrink-0">All Insights <ArrowRight size={15} /></Link>
              </Reveal>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {(posts || []).slice(0, 2).map((post, i) => (
                <Reveal key={post.id} delay={i * 0.06}>
                  <Link to={`/insights/${post.slug}`} className="card card-hover flex h-full flex-col overflow-hidden">
                    <div className="aspect-[16/9] overflow-hidden bg-sand">
                      {post.cover_image && <img src={post.cover_image} alt={post.title} loading="lazy" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gold-dark">{post.category}</span>
                      <h3 className="mt-2 font-serif text-xl font-semibold leading-snug text-navy">{post.title}</h3>
                      <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-mist">{post.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy">
                        Read Insight <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {(testimonials || []).length > 0 && (
        <section className="section-pad bg-sand" data-testid="testimonials-section">
          <div className="container-x">
            <SectionHead
              eyebrow="Testimonials"
              title="What Our Buyers Say"
              sub="Feedback from businesses we work with across international markets."
              center
            />
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal key={t.id} delay={(i % 3) * 0.05}>
                  <div className="card card-hover flex h-full flex-col p-6" data-testid={`testimonial-card-${t.id}`}>
                    {t.rating > 0 && (
                      <div className="flex gap-0.5 text-gold-dark" aria-label={`${t.rating} out of 5 stars`}>
                        {[...Array(5)].map((_, s) => (
                          <Star key={s} size={14} fill={s < t.rating ? "currentColor" : "none"} strokeWidth={1.5} />
                        ))}
                      </div>
                    )}
                    <p className="mt-3 flex-1 text-sm italic leading-relaxed text-ink">“{t.text}”</p>
                    <div className="mt-5 flex items-center gap-3 border-t border-line pt-4">
                      {t.photo_url ? (
                        <img src={t.photo_url} alt={t.client_name} className="h-10 w-10 rounded-full border border-line object-cover" loading="lazy" />
                      ) : (
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy font-serif text-sm font-semibold text-gold-soft">
                          {(t.client_name || "?").charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div>
                        <p className="text-sm font-bold text-navy">{t.client_name}</p>
                        <p className="text-xs text-mist">{[t.company, t.country].filter(Boolean).join(" · ")}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="section-pad" data-testid="home-faq-section">
        <div className="container-x grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          <SectionHead
            eyebrow="FAQ"
            title="Frequently Asked Questions"
            sub="Answers to the questions international buyers ask most. Anything else — our export team replies quickly on WhatsApp and email."
          />
          <div className="space-y-3">
            {(faqs || []).map((f, i) => (
              <Reveal key={f.id} delay={i * 0.03}>
                <details className="card group px-5 py-4 open:border-gold/50">
                  <summary className="cursor-pointer list-none text-sm font-bold text-navy marker:hidden [&::-webkit-details-marker]:hidden">
                    <span className="mr-2 text-gold-dark">+</span>
                    {f.question}
                  </summary>
                  <p className="mt-3 border-t border-line pt-3 text-sm leading-relaxed text-mist">{f.answer}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
